// CONFIG
export const COLORS = ["#C6654A", "#D59E5D", "#F8E877", "#97BD76", "#9999F9"];

/**
 * `top` for the avg pill when the average is above the highest band: centered
 * just above the bar like the PDF (pill is 16px tall: half of it + 2px gap).
 */
export const AVG_PILL_ABOVE_BAR_TOP = "-10px";

// The bands occupy the lower 89% of the bar container; the very-high cap is the top 11%.
const BAR_TOP_PCT = 11;
const BAR_SPAN_PCT = 89;
/** Lowest (red) band is never drawn thinner than this: 2% = 6px of the 300px bar. */
const MIN_RED_BAND_PCT = 2;

/**
 * Where each band sits (as a `top` %) with the lowest, red band given a minimum
 * height, matching the PDF. A tiny Very Low target (e.g. 0.01 on a 0-4 range)
 * would otherwise draw the red band under 1px, so it vanishes. The extra comes
 * from the tallest band; the others stay in proportion.
 *
 * `tops[i]` / `tops[i + 1]` are the bottom / top edge (as `top` %) of band `i`
 * (0 = lowest). Draw bands by index: when a target is 0 two boundaries share the
 * same value, which `toTop` can't tell apart. `toTop(v)` gives the `top` % for a
 * value on the bands as drawn, so the avg pill stays inside the right band.
 */
export function bandLayout(
    veryLow: number,
    low: number,
    medium: number,
    high: number
): { tops: number[]; toTop: (v: number) => number } {
    const t = [0, veryLow, low, medium, high];
    const h = [0, 1, 2, 3].map((i) => ((t[i + 1] - t[i]) / high) * BAR_SPAN_PCT);

    const deficit = MIN_RED_BAND_PCT - h[0];
    if (deficit > 0) {
        const donor = [1, 2, 3].reduce((best, i) => (h[i] > h[best] ? i : best), 1);
        const take = Math.min(deficit, h[donor] * 0.5);
        h[0] += take;
        h[donor] -= take;
    }

    const tops = [BAR_TOP_PCT + BAR_SPAN_PCT];
    for (let i = 0; i < 4; i++) {
        tops.push(tops[i] - h[i]);
    }

    const toTop = (v: number): number => {
        if (v >= high) return tops[4];
        if (v <= 0) return tops[0];
        for (let i = 0; i < 4; i++) {
            if (v >= t[i] && v <= t[i + 1] && t[i + 1] > t[i]) {
                return tops[i] - ((v - t[i]) / (t[i + 1] - t[i])) * h[i];
            }
        }
        return tops[0];
    };

    return { tops, toTop };
}

/**
 * Bar/band label format, matching the PDF's `_fmt`: whole numbers above 100,
 * otherwise up to 2 decimals (1000+ keeps its thousands separator).
 */
export function fmtBar(v: number): string {
    if (v >= 1000) return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
    if (v > 100) return Math.round(v).toString();
    return parseFloat(v.toFixed(2)).toString();
}

/** "% of Field" caption for a nutrient (up to 1 decimal), e.g. "1% of Field". */
export function fmtPercentOfField(v: number): string {
    return `${parseFloat(v.toFixed(1))}% of Field`;
}

export function fmt(v: number): string {
    if (v >= 1000) return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
    if (v >= 10) return parseFloat(v.toFixed(1)).toString();
    return parseFloat(v.toFixed(2)).toString();
}
